/**
 * Phase 14 — DictationField
 *
 * Drop-in replacement for `<Textarea>` that adds a microphone button.
 * Click → records audio via `MediaRecorder`. Click again → stops, posts
 * the audio to `/api/transcribe`, appends the transcribed text to the
 * textarea at the cursor position, and discards the audio buffer.
 *
 * Privacy guardrails:
 *   - Audio is held only as a `Blob` in browser memory between start &
 *     upload. After the network request resolves the Blob ref is dropped
 *     and the `MediaStream` tracks are stopped so the mic icon goes off.
 *   - The first time a user dictates, an unobtrusive consent banner
 *     explains where the audio goes (Whisper) and that it's discarded
 *     immediately. Persisted to localStorage so it shows once per user.
 *   - 120s hard cap — recording auto-stops at 120s with a toast so users
 *     can't accidentally upload longer files.
 *
 * Browser compat — falls back to a plain Textarea (no mic button) when
 * `MediaRecorder` is unavailable so the form still works.
 */
import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "../lib/api";

const CONSENT_KEY = "gw.dictation.consent.v1";
const MAX_DURATION_SEC = 120;

const _supportsRecorder =
  typeof window !== "undefined" &&
  typeof window.MediaRecorder !== "undefined" &&
  typeof navigator !== "undefined" &&
  typeof navigator.mediaDevices?.getUserMedia === "function";

function _pickMimeType() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const t of candidates) {
    try {
      if (window.MediaRecorder.isTypeSupported(t)) return t;
    } catch {
      /* ignore */
    }
  }
  return "";
}

export default function DictationField({
  value,
  onChange,
  targetField = "case_details",
  language = "en",
  testidPrefix,
  className,
  ...textareaProps
}) {
  const textareaRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const stopTimerRef = useRef(null);
  const tickRef = useRef(null);

  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const [showConsent, setShowConsent] = useState(false);

  useEffect(() => () => _cleanup({ recorderRef, streamRef, stopTimerRef, tickRef }), []);

  if (!_supportsRecorder) {
    // Browser doesn't support MediaRecorder — render plain Textarea.
    return (
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        className={className}
        {...textareaProps}
      />
    );
  }

  const startRecording = async () => {
    setError(null);

    // First-use consent gate
    if (typeof window !== "undefined" && !window.localStorage.getItem(CONSENT_KEY)) {
      setShowConsent(true);
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      const msg =
        e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError"
          ? "Microphone permission denied. Allow access in your browser to dictate."
          : "Couldn't access the microphone — check that no other app is using it.";
      setError(msg);
      toast.error(msg);
      return;
    }
    streamRef.current = stream;
    const mimeType = _pickMimeType();
    let recorder;
    try {
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch (e) {
      _cleanup({ recorderRef, streamRef, stopTimerRef, tickRef });
      setError("This browser couldn't start a recording session.");
      toast.error("Couldn't start a recording session.");
      return;
    }
    recorderRef.current = recorder;
    chunksRef.current = [];

    recorder.addEventListener("dataavailable", (ev) => {
      if (ev.data && ev.data.size) chunksRef.current.push(ev.data);
    });
    recorder.addEventListener("stop", async () => {
      const blob = new Blob(chunksRef.current, {
        type: mimeType || "audio/webm",
      });
      chunksRef.current = [];
      _stopStream(streamRef);
      await _uploadAndAppend({
        blob,
        targetField,
        language,
        textareaRef,
        value,
        onChange,
        setUploading,
        setError,
      });
    });

    recorder.start();
    setRecording(true);
    setElapsed(0);

    const startedAt = Date.now();
    tickRef.current = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);
    stopTimerRef.current = window.setTimeout(() => {
      toast.info(`Recording stopped at the ${MAX_DURATION_SEC}s limit.`);
      stopRecording();
    }, MAX_DURATION_SEC * 1000);
  };

  const stopRecording = () => {
    setRecording(false);
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch {
      /* ignore */
    }
  };

  const acceptConsent = () => {
    try {
      window.localStorage.setItem(CONSENT_KEY, "1");
    } catch {
      /* ignore */
    }
    setShowConsent(false);
  };

  return (
    <div className={"relative " + (className || "")}>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        {...textareaProps}
        // Reserve a little right-side padding so the mic button doesn't
        // overlap text. Keep the existing data-testid coming from caller.
        className={
          "pr-12 " + (textareaProps.className || "")
        }
      />

      <div
        className="absolute bottom-2 right-2 flex items-center gap-2"
        data-testid={
          testidPrefix ? `${testidPrefix}-dictation-controls` : "dictation-controls"
        }
      >
        {recording && (
          <span
            className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em]"
            style={{
              backgroundColor: "var(--danger-bg)",
              color: "var(--danger-fg)",
              boxShadow: "inset 0 0 0 1px var(--danger-border)",
            }}
            aria-live="polite"
          >
            <span
              aria-hidden
              className="inline-block h-1.5 w-1.5 animate-pulse rounded-full"
              style={{ backgroundColor: "var(--danger-fg)" }}
            />
            REC {String(Math.floor(elapsed / 60)).padStart(1, "0")}:
            {String(elapsed % 60).padStart(2, "0")}
          </span>
        )}
        {uploading && (
          <Loader2
            className="h-3.5 w-3.5 animate-spin"
            style={{ color: "var(--page-fg-muted)" }}
          />
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-label={recording ? "Stop dictation" : "Start dictation"}
          aria-pressed={recording}
          onClick={recording ? stopRecording : startRecording}
          disabled={uploading}
          data-testid={
            testidPrefix ? `${testidPrefix}-dictation-toggle` : "dictation-toggle"
          }
          className="h-7 w-7 p-0"
          style={
            recording
              ? {
                  backgroundColor: "var(--danger-bg)",
                  borderColor: "var(--danger-border)",
                  color: "var(--danger-fg)",
                }
              : undefined
          }
        >
          {recording ? (
            <Square className="h-3.5 w-3.5" strokeWidth={2} />
          ) : (
            <Mic className="h-3.5 w-3.5" strokeWidth={2} />
          )}
        </Button>
      </div>

      {error && (
        <p
          role="alert"
          className="mt-1 inline-flex items-start gap-1 text-[11px]"
          style={{ color: "var(--danger-fg)" }}
          data-testid={
            testidPrefix ? `${testidPrefix}-dictation-error` : "dictation-error"
          }
        >
          <AlertCircle className="mt-0.5 h-3 w-3" strokeWidth={2} />
          <span>{error}</span>
        </p>
      )}

      {showConsent && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="dictation-consent-title"
          className="absolute right-0 top-full z-30 mt-2 w-80 rounded-lg p-3 shadow-lg"
          style={{
            backgroundColor: "var(--surface)",
            border: "1px solid var(--border-subtle)",
          }}
          data-testid={
            testidPrefix ? `${testidPrefix}-dictation-consent` : "dictation-consent"
          }
        >
          <p
            id="dictation-consent-title"
            className="text-xs font-semibold"
            style={{ color: "var(--page-fg)" }}
          >
            Voice dictation
          </p>
          <p
            className="mt-1.5 text-[11px] leading-relaxed"
            style={{ color: "var(--page-fg-secondary)" }}
          >
            Audio is sent over HTTPS to OpenAI Whisper for transcription, then
            discarded immediately — never stored on our servers. Press the mic
            again after dismissing this notice to start.
          </p>
          <div className="mt-3 flex justify-end">
            <Button
              size="sm"
              onClick={acceptConsent}
              className="goodwin-primary-btn h-7 px-2 text-[11px] text-white"
              data-testid={
                testidPrefix
                  ? `${testidPrefix}-dictation-consent-accept`
                  : "dictation-consent-accept"
              }
            >
              Got it — don't show again
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

async function _uploadAndAppend({
  blob,
  targetField,
  language,
  textareaRef,
  value,
  onChange,
  setUploading,
  setError,
}) {
  if (!blob || blob.size === 0) {
    setError("Recording was empty — please try again.");
    return;
  }
  setUploading(true);
  setError(null);

  const fd = new FormData();
  fd.append("audio", blob, `dictation.${_extFor(blob.type)}`);
  fd.append("target_field", targetField);
  if (language) fd.append("language", language);

  try {
    const { data } = await api.post("/transcribe", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const text = (data?.text || "").trim();
    if (!text) {
      toast.message("Whisper didn't pick up any speech — try again.");
      return;
    }
    _appendAtCursor(textareaRef, value, onChange, text);
    toast.success("Transcribed");
  } catch (err) {
    const status = err?.response?.status;
    let msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
    if (status === 413) msg = "Audio is over the 25 MB / 120 s limit.";
    if (status === 429)
      msg = "Too many transcriptions — please wait a moment and try again.";
    if (status === 401) msg = "Your session expired — please sign in again.";
    if (status === 503) msg = "Transcription is not configured on this deployment.";
    setError(msg);
    toast.error(msg);
  } finally {
    setUploading(false);
    // Drop reference defensively
    blob = null; // eslint-disable-line no-param-reassign
  }
}

function _appendAtCursor(textareaRef, value, onChange, insertion) {
  const ta = textareaRef.current;
  const v = value || "";
  if (!ta) {
    _emit(onChange, (v ? v + " " : "") + insertion);
    return;
  }
  const start = ta.selectionStart ?? v.length;
  const end = ta.selectionEnd ?? v.length;
  const before = v.slice(0, start);
  const after = v.slice(end);
  const sep =
    before && !before.match(/[\s\n]$/) && !insertion.startsWith(" ")
      ? " "
      : "";
  const next = before + sep + insertion + after;
  _emit(onChange, next);
  // Move cursor to the end of the inserted block on next paint
  window.setTimeout(() => {
    if (!textareaRef.current) return;
    const pos = (before + sep + insertion).length;
    try {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(pos, pos);
    } catch {
      /* noop */
    }
  }, 0);
}

function _emit(onChange, nextValue) {
  if (typeof onChange !== "function") return;
  // Mimic a synthetic event so parent setForm((s)=>({...s, foo: e.target.value})) works.
  onChange({ target: { value: nextValue } });
}

function _extFor(mimeType) {
  if (!mimeType) return "webm";
  if (mimeType.includes("webm")) return "webm";
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("mp4")) return "m4a";
  if (mimeType.includes("mpeg") || mimeType.includes("mp3")) return "mp3";
  if (mimeType.includes("wav")) return "wav";
  return "webm";
}

function _stopStream(streamRef) {
  try {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  } catch {
    /* noop */
  }
}

function _cleanup({ recorderRef, streamRef, stopTimerRef, tickRef }) {
  try {
    if (
      recorderRef.current &&
      recorderRef.current.state !== "inactive"
    ) {
      recorderRef.current.stop();
    }
  } catch {
    /* noop */
  }
  recorderRef.current = null;
  if (tickRef.current) {
    clearInterval(tickRef.current);
    tickRef.current = null;
  }
  if (stopTimerRef.current) {
    clearTimeout(stopTimerRef.current);
    stopTimerRef.current = null;
  }
  _stopStream(streamRef);
}
